import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { TranslateService } from '@ngx-translate/core';

import { PopupService } from 'src/app/services/general/popup.service';
import { ErrorService } from 'src/app/services/general/error.service';

import { Voucher } from 'src/app/interfaces/voucher';
import { ProductType } from 'src/app/types/product';
import { Item } from 'src/app/interfaces/transaction';
import { DefaultAccountVoucherList, PromoAccountVoucherList } from 'src/app/commons/voucher';
import { set, subDays } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class VoucherService implements OnInit, OnDestroy {

  /**
   * Constructor
   * @param fns angularfire functions
   * @param translate translate service
   * @param functionService function service
   * @param popupService popup service
   */
  constructor(
    private fns: AngularFireFunctions,
    private translate: TranslateService,
    private popupService: PopupService,
    private errorService: ErrorService
  ) {
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
  }

  getSmsPromo() {
    return this.getCurrentPromo() ? true : false;
  }

  getCurrentPromo(): string {
    const date: Date = new Date();
    const specialPromoVoucher = this.getSpecialPromoVoucher(date);
    if (specialPromoVoucher) {
      return specialPromoVoucher;
    } else {
      const defaultVoucher = this.getDefaultVoucher(date);
      if (defaultVoucher) {
        return defaultVoucher;
      }
    }
    return '';
  }

  getSpecialPromoVoucher(date?: Date, revertStartDays?: number): string {
    try {
      if (!date) {
        date = new Date();
      }
      const index = PromoAccountVoucherList.findIndex((voucher: Voucher) => {
        if (voucher?.voucherId && voucher?.enable) {
          if (voucher?.startDate && voucher?.endDate) {
            const start = set(new Date(voucher.startDate + 'T00:00:00'), { hours: 0, minutes: 0, seconds: 0 });
            const end = set(new Date(voucher.endDate + 'T23:59:59'), { hours: 23, minutes: 59, seconds: 59 });
            if (start && end) {
              if (date >= start && date <= end) {
                return true;
              } else if (revertStartDays) {
                const newStartDate = subDays(start, revertStartDays);
                if (date >= newStartDate && date <= end) {
                  return true;
                }
              }
            }
          }
        }
        
        return false;
      });
      if (index !== -1) {
        return PromoAccountVoucherList[index].voucherId;
      }
      return '';
    } catch(err) {
      console.log(err);
    }
  }

  getSpecialPromoVoucherById(voucherId: string): Voucher {
    const index = PromoAccountVoucherList.findIndex((voucher: Voucher) => {
      return voucher.voucherId === voucherId;
    });
    return index!== -1 && PromoAccountVoucherList[index] ? PromoAccountVoucherList[index] : null;
  }

  getDefaultVoucher(date?: Date): string {
    if (!date) {
      date = new Date();
    }
    const month: number = date.getMonth();
    if (month >= 0 && DefaultAccountVoucherList?.[month]) {
      return DefaultAccountVoucherList?.[month];
    }
    return '';
  }

  /**
   * Validate voucher
   * @param voucherId voucher id
   * @returns voucher
   */
  async validateVoucher(voucherId: string, productType: ProductType, itemList?: Item[], bulkMode?: boolean, accountId?: string): Promise<Voucher> {
    let voucher: Voucher;
    if (voucherId) {
      voucher = await this.getVoucherCall(voucherId);
      if (voucher) {
        const now = new Date();

        const qty = itemList?.reduce((a: number, b: Item) => {
          return a + b?.qty ? b?.qty : 0;
        }, 0);
        if (!voucher.enable) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.enable'));
          return null;
        } else if (voucher?.startDate && new Date(voucher.startDate + 'T00:00:00') > now) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.start'));
          return null;
        } else if (voucher?.endDate && new Date(voucher.endDate + 'T23:59:59') < now) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.end'));
          return null;
        } else if (voucher?.startTimestamp && new Date(voucher.startTimestamp) > now) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.start'));
          return null;
        } else if (voucher?.endTimestamp && new Date(voucher.endTimestamp) < now) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.end'));
          return null;
        } else if (!voucher.unlimited && voucher.qty <= 0) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.qty'));
          return null;
        } else if (voucher?.productType && voucher.productType !== productType) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.invalid'));
          return null;
        } else if (voucher?.accountId && voucher.accountId !== accountId) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.invalid_account'));
          return null;
        } else if (bulkMode && qty > 1 && voucher?.maxQty === 1) {
          this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.single_account'));
          // return voucher;
        } else if (voucher?.minQty && voucher?.minQty > qty) {
          // this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.min_qty', { min: voucher.minQty }));
          // return voucher;
        } else if (voucher?.productId) {
          let flag = true;
          itemList?.forEach((item: Item) => {
            if (flag && item?.productId && voucher.productId !== item.productId) {
                flag = false;
            }
          });
          if (!flag) {
            this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.invalid_product_id'));
            // return null;
          }
        }
      }
    }
    return voucher;
  }

  /**
   * Get voucher fns call
   * @param voucherId voucher id
   * @returns voucher
   */
  async getVoucherCall(voucherId: string): Promise<Voucher> {
    return await this.fns.httpsCallable('getVoucherCall')({
      voucherId
    }).toPromise().then(async (voucher: Voucher) => {
      if (voucher?.voucherId === voucherId) {
        return voucher;
      } else {
        this.popupService.presentAlert(this.translate.instant('VOUCHER.validation.invalid'));
        return null;
      }
    }).catch((err: any) => {
      this.popupService.presentAlert(this.translate.instant('MSG.action_error'));
      this.errorService.logError(err);
      return null;
    });
  }

  async refundCallV2() {
    // await this.fns.httpsCallable('refundCallV2')({ transactionId: 'Oneksenlp5DDzNqZ9ohl', refundId: 'pyr_1RLxIQDkTCKjUS107XzGgkop', reason: 'requested_by_admin', triggerBy: 'admin' }).toPromise().then(async (response) => {
    //     console.log("🚀 ~ file: sms-payment.service.ts ~ line 483 ~ SmsPaymentService ~ this.fns.httpsCallable ~ response", response)
    // }).catch((err: any) => {
    //     console.log("🚀 ~ file: sms-payment.service.ts ~ line 485 ~ SmsPaymentService ~ this.fns.httpsCallable ~ err", err)
    // });
  }

  // async blastEmailCall(accountId: string) {
  //   await this.fns.httpsCallable('blastEmailCall')({ accountId }).toPromise().then(async (response) => {
  //       console.log("🚀 ~ file: sms-payment.service.ts ~ line 483 ~ SmsPaymentService ~ this.fns.httpsCallable ~ response", response)
  //   }).catch((err: any) => {
  //       console.log("🚀 ~ file: sms-payment.service.ts ~ line 485 ~ SmsPaymentService ~ this.fns.httpsCallable ~ err", err)
  //   });
  // }

  // async createInboxReq() {
  //   await this.fns.httpsCallable('createInboxReq')({  }).toPromise().then(async (response) => {
  //       console.log("🚀 ~ file: sms-payment.service.ts ~ line 483 ~ SmsPaymentService ~ this.fns.httpsCallable ~ response", response)
  //   }).catch((err: any) => {
  //       console.log("🚀 ~ file: sms-payment.service.ts ~ line 485 ~ SmsPaymentService ~ this.fns.httpsCallable ~ err", err)
  //   });
  // }



}
